/**
 * CameraManager constants — extracted to a plain .ts file so that
 * CameraManager.tsx (a React component file) satisfies the
 * react-refresh/only-export-components rule.
 */

import type {
  CameraConfig,
  AutoRotateConfig,
  AutoRotateSpeedPreset,
  CameraPreset,
} from '../api/types';

// ---------------------------------------------------------------------------
// Defaults (from docs/ims-engine-api/08-feature-camera-views.md)
// ---------------------------------------------------------------------------

export const DEFAULT_CAMERA: Required<CameraConfig> = {
  fov: 50,
  minPolarAngle: Math.PI * 0.2,
  maxPolarAngle: Math.PI * 0.48,
  minAzimuthAngle: -Infinity,
  maxAzimuthAngle: Infinity,
  minDistance: 4,
  maxDistance: 12,
  smoothTime: 0.25,
  draggingSmoothTime: 0.125,
  truckSpeed: 0,
  target: { x: 0, y: 0.4, z: 0 },
  maxHFov: 65,
};

/** Speed values for the three named auto-rotate presets (rad/s). */
export const AUTOROTATE_SPEED_PRESETS: Record<AutoRotateSpeedPreset, number> = {
  slow: 0.2,
  moderate: 0.5,
  fast: 1.0,
};

export const DEFAULT_AUTO_ROTATE: Required<AutoRotateConfig> = {
  enabled: true,
  speed: 0.5,
  speedPreset: 'moderate',
  pauseOnInteraction: true,
  resumeDelay: 3000,
  easeInDuration: 1500,
  invertRotation: false,
};

// ---------------------------------------------------------------------------
// Camera Presets
// ---------------------------------------------------------------------------

/**
 * Predefined camera behaviour presets.
 * Each preset overrides smoothTime, draggingSmoothTime, and speed parameters.
 */
export const CAMERA_PRESETS: Record<CameraPreset, Partial<CameraConfig>> = {
  /** Balanced — good for general use. */
  default: {
    smoothTime: 0.25,
    draggingSmoothTime: 0.125,
  },
  /** Heavy damping — slow, cinematic movements for showroom / hero shots. */
  cinematic: {
    smoothTime: 0.8,
    draggingSmoothTime: 0.4,
  },
  /** Minimal damping — instant response for configurator / power users. */
  snappy: {
    smoothTime: 0.05,
    draggingSmoothTime: 0.025,
  },
  /**
   * 360° Interior panorama — wide FOV, full look-around, locked at sphere centre.
   * Applied automatically by the engine on showPano(); no need to call manually.
   */
  pano: {
    fov: 70,
    maxHFov: Infinity,
    smoothTime: 0.15,
    draggingSmoothTime: 0.2,
    // Clamp vertical look: prevent looking straight up/down.
    // 0.15*PI ≈ 27° from top, 0.85*PI ≈ 27° from bottom.
    // Adjust these two values to taste (range: 0 … Math.PI).
    minPolarAngle: Math.PI * 0.35,
    maxPolarAngle: Math.PI * 0.5,
    minAzimuthAngle: -Infinity,
    maxAzimuthAngle: Infinity,
    minDistance: 0.001,
    maxDistance: 0.001,
  },
};
